'use client';

import { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { MicrocopyBySet } from './types';

const MicrocopyBySetContext = createContext<MicrocopyBySet>({});

export const MicrocopyBySetProvider = ({
  children,
  data,
}: PropsWithChildren<{ data: MicrocopyBySet }>) => {
  // Merge with any parent MicrocopyBySet context so nested providers add their
  // sets instead of replacing inherited ones (e.g. a page-level set on top of
  // the app-wide `product-card` set seeded in the locale layout).
  const parent = useContext(MicrocopyBySetContext);
  const value = useMemo(() => {
    // Merge per-set and skip empty incoming sets: an empty `{}` (what a
    // failed/empty upstream fetch yields for a set) must not overwrite a good
    // inherited set, e.g. a nested provider clobbering the app-wide
    // `product-card` set and silently dropping cards back to English.
    const merged: MicrocopyBySet = { ...parent };
    for (const [set, values] of Object.entries(data)) {
      if (values && Object.keys(values).length > 0) {
        merged[set] = values;
      }
    }
    return merged;
  }, [parent, data]);
  return (
    <MicrocopyBySetContext.Provider value={value}>
      {children}
    </MicrocopyBySetContext.Provider>
  );
};

export const useMicrocopy = (
  set: string,
  defaultValues: Record<string, string>
) => {
  const microcopy = useContext(MicrocopyBySetContext);

  return {
    ...defaultValues,
    ...(microcopy[set] || {}),
  } as Record<string, string>;
};
